import React, { ReactElement, useState } from 'react';
import { UnmountClosed } from 'react-collapse';
import { useSelector } from 'react-redux';
import {
  BodyText,
  InfoIcon,
  NextIcon,
  Spacer,
  Subtitle,
  TinyTitleBold,
  resetButtonStyle
} from '@kvdbil/components';
import styled from 'styled-components';
import { useTranslation } from '~/Locale';
import { formatMoney, inclVATtoExclVAT } from '~/helpers/currency';
import Auction from '~/App/shared/interfaces/Auction';
import { getBiddingFeeForMember } from '~/helpers/bid';
import { session as sessionSelector } from '~/App/shared/selectors/sessionSelector';
import { AlignIcon } from '~/App/shared/components/Layout/AlignIcon';
import { auctionTypeIsFixedPrice } from '~/helpers/buyCar';
import useShowExclVat from '~/App/shared/hooks/useShowExclVat';
import Link from '~/App/shared/components/Link';

export const PriceInformationContainer = styled.div`
  text-align: center;
`;

const PriceInformationItem = styled.li`
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayscaleToned.light6};
  padding: 0.7rem 0;
`;

const PriceInformationRowContainer = styled.button`
  ${resetButtonStyle};
  width: 100%;
  text-align: left;
  display: flex;
  flex-direction: column;
`;

export const PriceInformationRow = styled.div<{ expanded: boolean }>`
  display: flex;
  width: 100%;
  flex-direction: row;
  justify-content: space-between;

  svg {
    font-size: 1.5rem;
    margin-left: 0.5rem;
    color: ${({ expanded, theme }) =>
      expanded ? theme.colors.text.dark : theme.colors.grayscaleToned.dark5};
  }

  &:hover {
    svg {
      color: ${({ theme }) => theme.colors.text.dark};
    }
  }
`;

const PriceInformationCollapse = styled(BodyText)`
  text-align: left;
  padding: 0.3em 0;
`;

const StyledLink = styled(Link)`
  display: inline-flex;
  align-items: center;
`;

interface Props {
  auctionObject: Auction;
  isRecreationalVehicle: boolean;
  releasingCost?: number;
}

interface PriceInformationType {
  title: string;
  value: string;
  expanded: boolean;
  onClick(): void;
  contentExpanded: string | ReactElement;
}

type HandleStateChangeKey =
  | 'isCarDealerPriceOpen'
  | 'isCommissionOpen'
  | 'isCommissionExclVatOpen'
  | 'isMediationFeeOpen'
  | 'isMediationFeeExclVatOpen'
  | 'isVatOpen'
  | 'isExportOpen'
  | 'isMonthlyCostOpen';

const PriceInformation = ({ auctionObject, isRecreationalVehicle }: Props) => {
  const session = useSelector(sessionSelector);
  const { t } = useTranslation();
  const [state, setState] = useState({
    isCarDealerPriceOpen: false,
    isCommissionOpen: false,
    isCommissionExclVatOpen: false,
    isMediationFeeOpen: false,
    isMediationFeeExclVatOpen: false,
    isVatOpen: false,
    isExportOpen: false,
    isMonthlyCostOpen: false
  });

  const handleStateChange = (key: HandleStateChangeKey) => {
    setState(prevState => ({
      ...prevState,
      [key]: Boolean(!prevState[key])
    }));
  };

  const {
    isVatOpen,
    isExportOpen,
    isCommissionOpen,
    isCommissionExclVatOpen,
    isMediationFeeOpen,
    isMediationFeeExclVatOpen,
    isCarDealerPriceOpen
  } = state;

  const carDealerPrice =
    auctionObject?.processObject?.valuation?.carDealerPrice;
  const hasDeductibleVat = auctionObject?.processObject?.deductibleVat;
  const isExportable = auctionObject?.processObject?.exportable;
  const hasValidCarDealerprice =
    auctionObject?.processObject?.valuation?.carDealerPrice &&
    carDealerPrice > 0;
  const member = session?.member;
  const isAuthenticated = session?.isAuthenticated;
  const isOrganisation = member?.memberType === 'ORGANISATION_PERSON';
  const isFixedPriceObject = auctionTypeIsFixedPrice(auctionObject.auctionType);
  const biddingFee = getBiddingFeeForMember(member, auctionObject);
  const mediationFeeAmount = isOrganisation
    ? auctionObject.mediationFeeCorporate
    : auctionObject.mediationFee;

  const showExclVat = useShowExclVat(auctionObject?.processObject);

  const priceInformationList: PriceInformationType[] = [];
  priceInformationList.push({
    expanded: isCarDealerPriceOpen,
    title: t('Car dealer price'),
    value: hasValidCarDealerprice
      ? formatMoney({
          value: carDealerPrice,
          currency: t('SEK')
        })
      : t('Price missing'),
    onClick: () => handleStateChange('isCarDealerPriceOpen'),
    contentExpanded: t('Approximate cost at car dealer')
  });

  if (isFixedPriceObject && typeof mediationFeeAmount === 'number') {
    priceInformationList.push({
      expanded: isMediationFeeOpen,
      title: t('Mediation fee'),
      value: formatMoney({
        value: mediationFeeAmount,
        currency: t('SEK')
      }),
      onClick: () => handleStateChange('isMediationFeeOpen'),
      contentExpanded: t('Mediation fee description')
    });

    if (showExclVat) {
      priceInformationList.push({
        expanded: isMediationFeeExclVatOpen,
        title: t('Mediation fee excl VAT'),
        value: formatMoney({
          value: inclVATtoExclVAT(mediationFeeAmount),
          currency: t('SEK')
        }),
        onClick: () => handleStateChange('isMediationFeeExclVatOpen'),
        contentExpanded: t('Mediation fee description excl VAT')
      });
    }
  }

  if (!isFixedPriceObject) {
    priceInformationList.push({
      expanded: isCommissionOpen,
      title: isOrganisation ? t('Bidding fee corporate') : t('Bidding fee'),
      value: formatMoney({
        value: biddingFee,
        currency: t('SEK')
      }),
      onClick: () => handleStateChange('isCommissionOpen'),
      contentExpanded: isAuthenticated
        ? t('Bidding fee description')
        : t('Bidding fee description not logged in')
    });

    if (showExclVat) {
      priceInformationList.push({
        expanded: isCommissionExclVatOpen,
        title: isOrganisation
          ? t('Bidding fee corporate excl VAT')
          : t('Bidding fee excl VAT'),
        value: formatMoney({
          value: inclVATtoExclVAT(biddingFee),
          currency: t('SEK')
        }),
        onClick: () => handleStateChange('isCommissionExclVatOpen'),
        contentExpanded: isAuthenticated
          ? t('Bidding fee description excl VAT')
          : t('Bidding fee description not logged in')
      });
    }
  }

  priceInformationList.push(
    {
      expanded: isVatOpen,
      title: t('Deductible VAT'),
      value: hasDeductibleVat ? t('Yes') : t('No'),
      onClick: () => handleStateChange('isVatOpen'),
      contentExpanded: t('Information for enterprise buyers')
    },
    {
      expanded: isExportOpen,
      title: t('Export'),
      value: isExportable ? t('Yes') : t('No'),
      onClick: () => handleStateChange('isExportOpen'),
      contentExpanded: (
        <span>
          {`${t('Information about export')} `}
          <StyledLink to={`/${t('ROUTES.EXPORT')}`}>
            {t('Read our conditions')}
            <NextIcon />
          </StyledLink>
        </span>
      )
    }
  );

  const handledPriceInformationList = isRecreationalVehicle
    ? priceInformationList.slice(1)
    : priceInformationList;

  return (
    <PriceInformationContainer>
      <Subtitle as="h3">{t('Price information')}</Subtitle>

      <Spacer height={1.5} />

      <ul>
        {handledPriceInformationList.map((priceInformation, index) => {
          const id = `price-info-content-${index}`;
          return (
            <PriceInformationItem key={priceInformation.title}>
              <PriceInformationRowContainer
                onClick={priceInformation.onClick}
                aria-expanded={priceInformation.expanded}
                aria-controls={id}
              >
                <PriceInformationRow expanded={priceInformation.expanded}>
                  <BodyText as="span">{priceInformation.title}</BodyText>
                  <TinyTitleBold as="span">
                    <AlignIcon left={0.5}>
                      <span>{priceInformation.value}</span>
                      <InfoIcon />
                    </AlignIcon>
                  </TinyTitleBold>
                </PriceInformationRow>
              </PriceInformationRowContainer>
              <UnmountClosed isOpened={priceInformation.expanded}>
                <PriceInformationCollapse as="div" id={id}>
                  {priceInformation.contentExpanded}
                </PriceInformationCollapse>
              </UnmountClosed>
            </PriceInformationItem>
          );
        })}
      </ul>
    </PriceInformationContainer>
  );
};

export default PriceInformation;
